import * as React from 'react';
import { useFetch, useParams } from '../hooks';
import { QueryParams } from '../hooks/useParams';
import { SearchContext, searchReducer } from './Context';

const SearchProvider: React.FC<
  { endpoint: string; sortOrder: string | undefined } & React.PropsWithChildren
> = ({ endpoint, sortOrder, children }) => {
  const firstLoad = React.useRef(true);

  const onPopState = React.useCallback((payload: QueryParams) => {
    dispatch({ type: 'UPDATE_PARAMS', payload });
  }, []);

  const {
    queryParams: { query, page, sort, selectedFacets },
    updateQueryParams
  } = useParams(onPopState);

  const [state, dispatch] = React.useReducer(searchReducer, {
    query,
    page,
    sort: sortOrder ? sortOrder.split(',')[0] : sort,
    facets: [],
    selectedFacets,
    results: [],
    resultsTotal: 0,
    loading: false
  });

  const { data, loading } = useFetch(endpoint, state);

  const updateQuery = (payload: string) => {
    dispatch({ type: 'UPDATE_QUERY', payload });
  };

  const updatePage = (payload: number) =>
    dispatch({ type: 'UPDATE_PAGE', payload });

  const updateSort = (payload: string) =>
    dispatch({ type: 'UPDATE_SORT', payload });

  const addFacet = (category: string, facet: string) =>
    dispatch({ type: 'ADD_FACET', payload: { category, facet } });

  const removeFacet = (category: string, facet: string) =>
    dispatch({ type: 'REMOVE_FACET', payload: { category, facet } });

  const clearFacets = () => dispatch({ type: 'CLEAR_FACETS' });

  React.useEffect(() => {
    if (!firstLoad.current) {
      updateQueryParams({
        query: state.query,
        page: state.page,
        sort: state.sort,
        selectedFacets: state.selectedFacets
      });

      dispatch({ type: 'SET_DATA', payload: data });
    } else {
      firstLoad.current = false;
    }
  }, [data]);

  return (
    <SearchContext.Provider
      value={{
        state: { ...state, loading },
        updateQuery,
        updatePage,
        updateSort,
        addFacet,
        removeFacet,
        clearFacets
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export default SearchProvider;
